import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    initialStep: { type: Number, default: 0 },
    hasOnboardingMaterials: { type: Boolean, default: false },
    hasRequiredAttestations: { type: Boolean, default: false },
    showEmptyMaterialSteps: { type: Boolean, default: false }
  }

  static targets = [
    "step",
    "stepNumber",
    "stepLabel",
    "stepName",
    "progressBar",
    "progressItem",
    "overviewItem",
    "backButton",
    "nextButton",
    "submitButton",
    "review",
    "sportsSearch",
    "sportsOption",
    "sportsEmpty",
    "dob",
    "concussionCount",
    "concussionDateField",
    "registrantKind",
    "personaPath",
    "personaPathBody",
    "personaPathStatus",
    "registrantDetails",
    "guardianDetails",
    "adultApprovalNotice",
    "authorizedContactToggle",
    "authorizedContactSection",
    "authorizedContactItem",
    "authorizedContactTypeToggle",
    "authorizedContactPanel",
    "authorizedContactDetails",
    "contactTypeOtherField",
    "informationSharingHelp",
    "reviewIntro",
    "guardianApprovalFields",
    "athleteEmailField",
    "athleteEmail",
    "athleteEmailOptionalMark",
    "athleteEmailHelp",
    "registrantFirstName",
    "registrantLastName",
    "registrantEmail",
    "registrantPhone",
    "registrantRelationship",
    "guardianSameAsRegistrant",
    "guardianManualDetails",
    "guardianFirstName",
    "guardianLastName",
    "guardianEmail",
    "guardianPhone",
    "guardianRelationship",
    "guardianSignature",
    "materialSignatureFields",
    "contactEmail",
    "contactEmailHelp",
    "contactPhone",
    "contactPhoneHelp",
    "contactChannelNotice",
    "allowedCategory",
    "allowedChannel",
    "allInformation"
  ]

  connect() {
    this.currentStep = this.initialStepValue
    this.validationErrorCounter = 0
    this.draftRestoring = false
    this.guardianSignatureWasEdited = false
    this.guardianSameAsRegistrantWasChanged = false
    this.stepNames = {
      0: "Basic information",
      1: "Sports",
      2: "Health history",
      3: "Access and communication",
      4: "Onboarding materials",
      5: "Attestations",
      6: "Overall review"
    }
    this.stepShortNames = {
      0: "Basic",
      1: "Sports",
      2: "Health",
      3: "Access",
      4: "Materials",
      5: "Attest",
      6: "Review"
    }
    this.restoreDraft()
    this.initializeDependents()
    this.refreshRegistrationAccess()
    this.showStep(false)
  }

  next() {
    if (!this.validateStep(this.currentStep)) return

    const stepOrder = this.orderedStepIndexes()
    const currentPosition = this.currentStepPosition(stepOrder)

    if (currentPosition < stepOrder.length - 1) {
      this.currentStep = stepOrder[currentPosition + 1]
      this.showStep()
    }
  }

  previous() {
    const stepOrder = this.orderedStepIndexes()
    const currentPosition = this.currentStepPosition(stepOrder)

    if (currentPosition > 0) {
      this.currentStep = stepOrder[currentPosition - 1]
      this.showStep()
    }
  }

  validateSubmission(event) {
    const stepOrder = this.orderedStepIndexes()

    for (const index of stepOrder) {
      if (!this.stepIsValid(index)) {
        event.preventDefault()
        this.currentStep = index
        this.showStep()
        this.validateStep(index)
        return
      }
    }
  }

  validateStep(index) {
    const controls = this.stepControls(index)
    const invalidControls = controls.filter((control) => !control.checkValidity())

    this.clearStepValidation(index)

    if (invalidControls.length === 0) return true

    const invalidFields = new Set()
    invalidControls.forEach((control) => {
      const field = this.validationFieldFor(control)
      if (invalidFields.has(field)) return

      invalidFields.add(field)
      this.showValidationError(control, field)
    })

    const firstInvalidControl = invalidControls[0]
    const firstInvalidField = this.validationFieldFor(firstInvalidControl)

    this.focusValidationControl(firstInvalidControl)
    firstInvalidField.scrollIntoView({ behavior: "smooth", block: "center" })
    return false
  }

  showStep(shouldScroll = true) {
    const stepOrder = this.orderedStepIndexes()

    if (!stepOrder.includes(this.currentStep)) {
      this.currentStep = stepOrder[0]
    }

    const currentPosition = this.currentStepPosition(stepOrder)

    this.stepTargets.forEach((step, index) => {
      step.hidden = index !== this.currentStep
    })

    this.progressItemTargets.forEach((item, index) => {
      const orderedStepIndex = stepOrder[index]
      const label = item.querySelector("small")
      const number = item.querySelector("span")

      item.hidden = orderedStepIndex === undefined

      if (orderedStepIndex !== undefined) {
        if (number) number.textContent = index + 1
        if (label) label.textContent = this.stepShortNames[orderedStepIndex]
      }

      item.classList.toggle("is-current", index === currentPosition)
      item.classList.toggle("is-complete", index < currentPosition)
      if (index === currentPosition) {
        item.setAttribute("aria-current", "step")
      } else {
        item.removeAttribute("aria-current")
      }
    })

    this.overviewItemTargets.forEach((item, index) => {
      const stepIndex = Number(item.dataset.registrationStepIndex ?? index)
      const position = stepOrder.indexOf(stepIndex)
      const number = item.querySelector("span")

      item.hidden = position < 0
      if (number && position >= 0) number.textContent = position + 1
    })

    this.stepNumberTargets.forEach((target, index) => {
      const position = stepOrder.indexOf(index)
      target.textContent = position >= 0 ? `Step ${position + 1}` : ""
    })

    this.stepLabelTarget.textContent = `Step ${currentPosition + 1} of ${stepOrder.length}`
    this.stepNameTarget.textContent = this.stepNames[this.currentStep]
    this.progressBarTarget.style.width = `${((currentPosition + 1) / stepOrder.length) * 100}%`

    this.backButtonTarget.hidden = currentPosition === 0
    this.nextButtonTarget.hidden = currentPosition === stepOrder.length - 1
    this.submitButtonTarget.hidden = currentPosition !== stepOrder.length - 1

    if (currentPosition === stepOrder.length - 1) this.buildReview()

    this.refreshActionState()
    this.refreshSubmitLabel()

    if (shouldScroll) {
      this.element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  refreshActionState() {
    const readyToSubmit = this.orderedStepIndexes().every((index) => this.stepIsValid(index))

    this.clearResolvedValidationErrors()
    this.setButtonState(this.nextButtonTarget, false)
    this.setButtonState(this.submitButtonTarget, !readyToSubmit)
    this.saveDraft()
  }

  refreshSubmitLabel() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.value = this.currentPathCopy().submitLabel
  }

  toggleDependent(event) {
    const dependentId = event.currentTarget.dataset.dependentId
    if (!dependentId) return

    this.updateDependent(dependentId, true)
  }

  initializeDependents() {
    const dependentIds = new Set(
      Array.from(this.element.querySelectorAll("[data-dependent-id]"))
        .map((control) => control.dataset.dependentId)
        .filter(Boolean)
    )

    dependentIds.forEach((dependentId) => this.updateDependent(dependentId, false))
  }

  refreshRegistrationAccess() {
    const subjectIsMinor = this.subjectAge() !== null && this.subjectAge() < 18
    const registrantKind = this.selectedRegistrantKind()
    const registrantIsSomeoneElse = registrantKind === "someone_else"
    const minorCompletingOwnRegistration = this.minorCompletingOwnRegistration()
    const informationSharingAvailable = !minorCompletingOwnRegistration
    const addingAuthorizedContact = informationSharingAvailable && this.selectedAuthorizedContactValue() === "1"
    const guardianCanSignNow = subjectIsMinor && registrantIsSomeoneElse

    this.updateAthleteEmailRequirement()
    this.togglePanel(this.registrantDetailsTarget, registrantIsSomeoneElse)
    this.togglePanel(this.guardianDetailsTarget, subjectIsMinor)
    this.togglePanel(this.guardianApprovalFieldsTarget, guardianCanSignNow)
    if (this.hasMaterialSignatureFieldsTarget) {
      this.togglePanel(this.materialSignatureFieldsTarget, this.registrationCompletesAtSubmit() && !subjectIsMinor)
    }
    this.togglePanel(this.adultApprovalNoticeTarget, !subjectIsMinor && registrantIsSomeoneElse)
    this.togglePanel(this.authorizedContactSectionTarget, informationSharingAvailable)
    if (this.hasAuthorizedContactDetailsTarget) {
      this.togglePanel(this.authorizedContactDetailsTarget, addingAuthorizedContact)
    }
    this.updateAuthorizedContactCards(informationSharingAvailable)
    this.updatePersonaPath(subjectIsMinor, registrantKind)
    this.updatePathCopy(subjectIsMinor, registrantKind)
    this.updateContactChannelRequirements(addingAuthorizedContact)
    this.updateGuardianSameAsRegistrantState(subjectIsMinor && registrantIsSomeoneElse)
    this.syncGuardianFromRegistrant()
    this.syncGuardianSignature()
    this.refreshConcussionDate()

    this.refreshActionState()
  }

  refreshConcussionDate() {
    if (!this.hasConcussionCountTarget || !this.hasConcussionDateFieldTarget) return

    const count = Number.parseInt(this.concussionCountTarget.value, 10)
    const shouldShow = Number.isFinite(count) && count > 0 && !this.concussionCountTarget.disabled

    this.concussionDateFieldTarget.hidden = !shouldShow
    this.concussionDateFieldTarget.querySelectorAll("input, select, textarea").forEach((field) => {
      field.disabled = !shouldShow
      if (!shouldShow) field.value = ""
    })
  }

  updatePersonaPath(subjectIsMinor, registrantKind) {
    if (!this.hasPersonaPathTarget) return

    const age = this.subjectAge()
    const content = this.personaPathContent(age, subjectIsMinor, registrantKind)

    this.personaPathTarget.dataset.personaPath = content.key
    this.personaPathBodyTarget.textContent = content.body
    this.personaPathStatusTarget.textContent = content.status
  }

  updatePathCopy(subjectIsMinor, registrantKind) {
    const content = this.pathCopy(subjectIsMinor, registrantKind)

    if (this.hasInformationSharingHelpTarget) {
      this.informationSharingHelpTarget.textContent = content.informationSharingHelp
    }

    if (this.hasReviewIntroTarget) {
      this.reviewIntroTarget.textContent = content.reviewIntro
    }

    this.refreshSubmitLabel()
  }

  currentPathCopy() {
    return this.pathCopy(this.subjectAge() !== null && this.subjectAge() < 18, this.selectedRegistrantKind())
  }

  pathCopy(subjectIsMinor, registrantKind) {
    if (registrantKind === null) {
      return {
        submitLabel: "Complete registration",
        reviewIntro: "Check your answers before submitting.",
        informationSharingHelp: "Use this only for people who should receive scheduling, care, report, or safety information."
      }
    }

    if (subjectIsMinor && registrantKind === "self") {
      return {
        submitLabel: "Request guardian approval",
        reviewIntro: "Check your starter information before requesting guardian approval.",
        informationSharingHelp: "A parent or legal guardian can add information-sharing contacts during approval."
      }
    }

    if (subjectIsMinor) {
      return {
        submitLabel: "Complete registration",
        reviewIntro: "Check your answers before completing this minor registration.",
        informationSharingHelp: "As parent or legal guardian, use this only for people who should receive scheduling, care, report, or safety information."
      }
    }

    if (registrantKind === "someone_else") {
      return {
        submitLabel: "Request athlete approval",
        reviewIntro: "Check your answers before sending this to the adult athlete for approval.",
        informationSharingHelp: "The adult athlete must approve any requested information sharing before it is active."
      }
    }

    return {
      submitLabel: "Complete registration",
      reviewIntro: "Check your answers before completing registration.",
      informationSharingHelp: "Use this only for people who should receive scheduling, care, report, or safety information."
    }
  }

  personaPathContent(age, subjectIsMinor, registrantKind) {
    if (registrantKind === null) {
      return {
        key: "unknown",
        body: "The registration path depends on the athlete's age and whether the athlete or someone else is completing this form.",
        status: "Choose who is completing the form so we can direct you to the right path."
      }
    }

    if (subjectIsMinor && registrantKind === "someone_else") {
      return {
        key: "guardian-minor",
        body: "A parent or legal guardian can complete registration and choose any trusted contacts now.",
        status: "This registration can be completed today if guardian authority and signature are provided."
      }
    }

    if (subjectIsMinor) {
      return {
        key: "minor-self",
        body: "You can start the registration process, but a parent or legal guardian must approve before your registration and access are active.",
        status: "After submit, we will request guardian approval and keep your registration pending until it is completed."
      }
    }

    if (registrantKind === "someone_else") {
      return {
        key: "helper-adult",
        body: "You can enter information for the athlete, but the adult athlete must approve before registration and requested access are active.",
        status: "After submit, we will send the adult athlete an approval request."
      }
    }

    return {
      key: "adult-self",
      body: "The athlete can complete registration and choose any trusted contacts now.",
      status: "This registration can be completed today."
    }
  }

  syncGuardianFromRegistrant() {
    if (!this.hasGuardianSameAsRegistrantTarget || !this.guardianSameAsRegistrantTarget.checked) return
    if (!this.hasGuardianFirstNameTarget || !this.hasRegistrantFirstNameTarget) return

    this.guardianFirstNameTarget.value = this.registrantFirstNameTarget.value
    this.guardianLastNameTarget.value = this.registrantLastNameTarget.value
    this.guardianEmailTarget.value = this.registrantEmailTarget.value
    this.guardianPhoneTarget.value = this.registrantPhoneTarget.value

    if (["parent", "legal_guardian"].includes(this.registrantRelationshipTarget.value)) {
      this.guardianRelationshipTarget.value = this.registrantRelationshipTarget.value
    }

    if (!this.guardianSignatureWasEdited) {
      this.syncGuardianSignature()
    }

    this.refreshActionState()
  }

  toggleGuardianSameAsRegistrant() {
    this.guardianSameAsRegistrantWasChanged = true
    this.updateGuardianManualDetails()
    this.syncGuardianFromRegistrant()
    this.refreshActionState()
  }

  updateGuardianSameAsRegistrantState(shouldShow) {
    if (!this.hasGuardianSameAsRegistrantTarget) return

    const wrapper = this.guardianSameAsRegistrantTarget.closest(".attestation-check")
    if (wrapper) wrapper.hidden = !shouldShow
    this.guardianSameAsRegistrantTarget.disabled = !shouldShow

    if (!shouldShow) {
      this.guardianSameAsRegistrantTarget.checked = false
    } else if (!this.guardianSameAsRegistrantWasChanged) {
      this.guardianSameAsRegistrantTarget.checked = true
    }

    this.updateGuardianManualDetails()
  }

  updateGuardianManualDetails() {
    if (!this.hasGuardianManualDetailsTarget || !this.hasGuardianSameAsRegistrantTarget) return

    this.guardianManualDetailsTarget.hidden = this.guardianSameAsRegistrantTarget.checked
  }

  syncGuardianSignature() {
    if (!this.hasGuardianSignatureTarget || this.guardianSignatureWasEdited) return

    const name = [this.guardianFirstNameTarget?.value, this.guardianLastNameTarget?.value]
      .map((value) => value?.trim())
      .filter(Boolean)
      .join(" ")

    this.guardianSignatureTarget.value = name
    this.guardianSignatureTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  markGuardianSignatureEdited() {
    this.guardianSignatureWasEdited = true
  }

  updateDependent(dependentId, clearWhenHidden) {
    const target = this.element.querySelector(`#${dependentId}`)
    const controls = Array.from(this.element.querySelectorAll(`[data-dependent-id="${dependentId}"]`))
    const selected = controls.find((control) => control.checked)
    const shouldShow = selected?.value === "true"

    target.hidden = !shouldShow
    target.querySelectorAll("input, select, textarea").forEach((control) => {
      control.disabled = !shouldShow

      if (!shouldShow && clearWhenHidden) {
        if (control.type === "checkbox" || control.type === "radio") {
          control.checked = false
        } else if (control.type !== "hidden") {
          control.value = ""
        }
      }
    })

    if (!shouldShow) this.clearValidationFor(target)
    this.refreshConcussionDate()
    this.refreshActionState()
  }

  filterSports() {
    const query = this.sportsSearchTarget.value.trim().toLowerCase()
    let visibleCount = 0

    this.sportsOptionTargets.forEach((option) => {
      const matches = option.dataset.sportName.includes(query)
      option.hidden = !matches
      if (matches) visibleCount += 1
    })

    this.sportsEmptyTarget.hidden = visibleCount > 0
  }

  toggleAllInformation(event) {
    event.preventDefault()
    event.stopPropagation()

    const fields = this.allowedInformationFields(event.currentTarget)
    const checkedCount = fields.filter((field) => field.checked).length
    const shouldCheck = checkedCount < fields.length

    fields.forEach((field) => {
      field.checked = shouldCheck
      field.setCustomValidity("")
    })

    this.refreshAllInformationState()
    this.refreshActionState()
  }

  refreshAllInformationState() {
    const controls = this.hasAllInformationTarget ? this.allInformationTargets : Array.from(this.element.querySelectorAll("[data-registration-all-information]"))
    if (controls.length === 0 || !this.hasAllowedCategoryTarget) return

    controls.forEach((allControl) => {
      const selectable = this.allowedInformationFields(allControl)
      const checkedCount = selectable.filter((field) => field.checked).length

      allControl.checked = selectable.length > 0 && checkedCount === selectable.length
      allControl.indeterminate = checkedCount > 0 && checkedCount < selectable.length
    })
  }

  allowedInformationFields(allControl) {
    const group = allControl.closest(".compact-options")
    if (!group) return this.allowedCategoryTargets

    const fields = Array.from(group.querySelectorAll("input[name='registration_intake[allowed_categories][]'], input[name$='[allowed_categories][]']"))
    return fields.length > 0 ? fields : this.allowedCategoryTargets
  }

  buildReview() {
    this.reviewTarget.replaceChildren()

    this.orderedStepIndexes().slice(0, -1).forEach((stepIndex) => {
      const step = this.stepTargets[stepIndex]
      const section = document.createElement("section")
      section.className = "registration-review__section"

      const heading = document.createElement("h3")
      heading.textContent = step.dataset.reviewSource
      section.appendChild(heading)

      const list = document.createElement("dl")
      step.querySelectorAll("[data-review-label]").forEach((field) => {
        if (this.hiddenWithinStep(field, step)) return

        const term = document.createElement("dt")
        term.textContent = field.dataset.reviewLabel

        const description = document.createElement("dd")
        description.textContent = this.reviewValue(field) || "Not provided"

        list.append(term, description)
      })

      section.appendChild(list)
      this.reviewTarget.appendChild(section)
    })
  }

  reviewValue(field) {
    const controls = Array.from(field.querySelectorAll("input, select, textarea"))
      .filter((control) => !control.disabled && control.type !== "hidden" && control.type !== "search")

    const checkableControls = controls.filter((control) =>
      control.type === "checkbox" || control.type === "radio"
    )
    const checkedControls = checkableControls.filter((control) => control.checked)

    if (checkedControls.length > 0) {
      return checkedControls.map((control) => this.readableControlValue(control, field)).join(", ")
    }

    if (checkableControls.length > 0) return ""

    const select = controls.find((control) => control.tagName === "SELECT")
    if (select) {
      return Array.from(select.selectedOptions).map((option) => option.text).filter(Boolean).join(", ")
    }

    return controls.find((control) => control.value.trim())?.value.trim() || ""
  }

  stepControls(index) {
    return Array.from(this.stepTargets[index].querySelectorAll("input, select, textarea"))
      .filter((control) => !control.disabled && control.type !== "hidden" && control.type !== "search")
  }

  stepIsValid(index) {
    return this.stepControls(index).every((control) => control.checkValidity())
  }

  clearStepValidation(index) {
    this.clearValidationFor(this.stepTargets[index])
  }

  clearValidationFor(scope) {
    if (!scope) return

    if (scope.matches?.(".registration-field--invalid, .health-question--invalid, .attestation-check--invalid")) {
      scope.classList.remove("registration-field--invalid", "health-question--invalid", "attestation-check--invalid")
    }
    scope.querySelectorAll(".registration-validation-error").forEach((error) => error.remove())
    scope.querySelectorAll(".registration-field--invalid, .health-question--invalid, .attestation-check--invalid").forEach((field) => {
      field.classList.remove("registration-field--invalid", "health-question--invalid", "attestation-check--invalid")
    })
    scope.querySelectorAll("[aria-invalid='true']").forEach((control) => {
      control.removeAttribute("aria-invalid")
      control.removeAttribute("aria-describedby")
    })
  }

  clearResolvedValidationErrors() {
    this.element.querySelectorAll(".registration-validation-error").forEach((error) => {
      const field = error.closest(".registration-field, .health-question, .attestation-check")
      if (!field) return

      const controls = Array.from(field.querySelectorAll("input, select, textarea"))
        .filter((control) => !control.disabled && control.type !== "hidden" && control.type !== "search")

      if (controls.every((control) => control.checkValidity())) {
        this.clearValidationFor(field)
      }
    })
  }

  showValidationError(control, field) {
    const message = document.createElement("p")
    const messageId = field.dataset.registrationValidationId || `registration-validation-error-${++this.validationErrorCounter}`
    field.dataset.registrationValidationId = messageId

    message.id = messageId
    message.className = "registration-validation-error"
    message.setAttribute("role", "alert")
    message.textContent = this.validationMessageFor(control, field)

    field.classList.add(this.invalidClassFor(field))
    this.describeInvalidControls(field, messageId)

    const choiceList = field.querySelector(".registration-choice-list, .binary-choice, .compact-options")
    if (choiceList) {
      choiceList.before(message)
    } else if (field.querySelector(".registration-field__help")) {
      field.querySelector(".registration-field__help").before(message)
    } else {
      field.appendChild(message)
    }
  }

  describeInvalidControls(field, messageId) {
    field.querySelectorAll("input, select, textarea").forEach((control) => {
      if (control.disabled || control.type === "hidden" || control.type === "search") return

      control.setAttribute("aria-invalid", "true")
      control.setAttribute("aria-describedby", messageId)
    })
  }

  focusValidationControl(control) {
    control.focus({ preventScroll: true })
  }

  validationFieldFor(control) {
    return control.closest(".registration-field, .health-question, .attestation-check") || control
  }

  invalidClassFor(field) {
    if (field.classList.contains("health-question")) return "health-question--invalid"
    if (field.classList.contains("attestation-check")) return "attestation-check--invalid"
    return "registration-field--invalid"
  }

  validationMessageFor(control, field) {
    if (control.validity.valueMissing) {
      if (control.type === "checkbox") {
        return "Check this box to continue."
      }

      if (control.type === "radio") {
        return `Choose ${this.validationLabelFor(control, field)}.`
      }

      if (control.tagName === "SELECT") {
        return `Select ${this.validationLabelFor(control, field)}.`
      }

      return `Enter ${this.validationLabelFor(control, field)}.`
    }

    if (control.validity.typeMismatch && control.type === "email") {
      return "Enter a valid email address."
    }

    if (control.validity.badInput || control.validity.typeMismatch) {
      return "Check this field and try again."
    }

    return control.validationMessage || "Check this field and try again."
  }

  validationLabelFor(control, field) {
    const label = field.querySelector("legend")?.textContent ||
      (control.id ? field.querySelector(`label[for="${control.id}"]`)?.textContent : null) ||
      control.closest("label")?.textContent ||
      field.dataset.reviewLabel ||
      "this field"

    return label.trim().replace(/\s+/g, " ").replace(/[.:?]*$/, "").toLowerCase()
  }

  orderedStepIndexes() {
    const approvalSteps = this.registrationCompletesAtSubmit() ? this.completionStepIndexes() : []

    if (this.minorCompletingOwnRegistration()) return [0, 3, 6]
    if (this.subjectAge() !== null && this.subjectAge() < 18) return [0, 3, 2, 1, ...approvalSteps, 6]

    return [0, 2, 1, 3, ...approvalSteps, 6]
  }

  completionStepIndexes() {
    const steps = []
    if (this.showEmptyMaterialStepsValue || this.hasOnboardingMaterialsValue) steps.push(4)
    if (this.showEmptyMaterialStepsValue || this.hasRequiredAttestationsValue) steps.push(5)
    return steps
  }

  registrationCompletesAtSubmit() {
    const age = this.subjectAge()
    const registrantKind = this.selectedRegistrantKind()

    if (age === null || registrantKind === null) return true

    const subjectIsMinor = age < 18
    return subjectIsMinor ? registrantKind === "someone_else" : registrantKind === "self"
  }

  minorCompletingOwnRegistration() {
    return this.subjectAge() !== null &&
      this.subjectAge() < 18 &&
      this.selectedRegistrantKind() === "self"
  }

  currentStepPosition(stepOrder) {
    const position = stepOrder.indexOf(this.currentStep)
    return position >= 0 ? position : 0
  }

  setButtonState(button, isDisabled) {
    button.disabled = isDisabled
    button.setAttribute("aria-disabled", isDisabled.toString())
  }

  saveDraft() {
    if (this.draftRestoring) return

    const key = this.draftStorageKey()
    const storage = this.draftStorage()
    if (!key || !storage) return

    const draft = {
      currentStep: this.currentStep,
      fields: this.draftControls().map((control) => this.serializeDraftControl(control))
    }

    try {
      storage.setItem(key, JSON.stringify(draft))
    } catch (_error) {
      // Storage may be unavailable in private browsing or locked-down webviews.
    }
  }

  restoreDraft() {
    const key = this.draftStorageKey()
    const storage = this.draftStorage()
    if (!key || !storage) return

    let draft
    try {
      draft = JSON.parse(storage.getItem(key))
    } catch (_error) {
      return
    }

    if (!draft || !Array.isArray(draft.fields)) return

    this.draftRestoring = true
    const savedFields = new Map(draft.fields.map((field) => [this.draftFieldKey(field), field]))

    this.draftControls().forEach((control) => {
      const savedField = savedFields.get(this.draftFieldKey(control))
      if (!savedField) return

      if (control.type === "checkbox" || control.type === "radio") {
        control.checked = savedField.checked
      } else {
        control.value = savedField.value ?? ""
      }
    })

    if (Number.isInteger(draft.currentStep)) {
      this.currentStep = draft.currentStep
    }
    this.draftRestoring = false
  }

  draftStorage() {
    try {
      return window.sessionStorage
    } catch (_error) {
      return null
    }
  }

  draftStorageKey() {
    const token = this.element.querySelector("input[name='registration_token']")?.value
    return token ? `registration-athlete:${token}:draft:v1` : null
  }

  draftControls() {
    return Array.from(this.element.querySelectorAll("input, select, textarea"))
      .filter((control) => this.shouldPersistDraftControl(control))
  }

  shouldPersistDraftControl(control) {
    return control.name &&
      !["button", "file", "hidden", "reset", "search", "submit"].includes(control.type)
  }

  serializeDraftControl(control) {
    return {
      name: control.name,
      type: control.type,
      value: control.value,
      checked: control.checked
    }
  }

  draftFieldKey(field) {
    return `${field.name}::${field.type}::${field.value ?? ""}`
  }

  readableControlValue(control, field) {
    if (control.dataset.reviewValue) return control.dataset.reviewValue

    if (control.id) {
      const explicitLabel = field.querySelector(`label[for="${control.id}"]`)
      if (explicitLabel) return explicitLabel.textContent.trim()
    }

    const wrappingLabel = control.closest("label")
    if (wrappingLabel && field.contains(wrappingLabel)) {
      const strongText = wrappingLabel.querySelector("strong")?.textContent.trim()
      return strongText || wrappingLabel.textContent.trim()
    }

    return control.value
  }

  hiddenWithinStep(field, step) {
    if (field.hidden) return true

    let element = field.parentElement

    while (element && element !== step) {
      if (element.hidden) return true
      element = element.parentElement
    }

    return false
  }

  togglePanel(panel, shouldShow) {
    if (!panel) return

    panel.hidden = !shouldShow
    panel.querySelectorAll("input, select, textarea").forEach((control) => {
      control.disabled = !shouldShow

      if (control.dataset.registrationRequiredWhenVisible !== undefined) {
        control.required = shouldShow
      }
    })

    if (!shouldShow) this.clearValidationFor(panel)
  }

  updateAthleteEmailRequirement() {
    if (!this.hasAthleteEmailTarget) return

    const age = this.subjectAge()
    const subjectIsAdult = age !== null && age >= 18

    this.athleteEmailTarget.disabled = false
    this.athleteEmailTarget.required = subjectIsAdult
    this.athleteEmailOptionalMarkTarget.hidden = subjectIsAdult
    this.athleteEmailHelpTarget.textContent = subjectIsAdult
      ? "Required for athletes 18 or older."
      : "Optional for minors. Guardian email is collected during approval."
  }

  updateContactChannelRequirements(addingAuthorizedContact) {
    if (this.hasAuthorizedContactPanelTarget) {
      this.authorizedContactPanelTargets.forEach((panel) => this.updateContactPanelChannelRequirements(panel))
      return
    }

    if (!this.hasContactPhoneTarget) return

    const selectedChannels = this.allowedChannelTargets
      .filter((control) => control.checked)
      .map((control) => control.value)
    const phoneRequired = addingAuthorizedContact && selectedChannels.some((channel) =>
      channel === "phone" || channel === "sms"
    )

    if (this.hasContactEmailTarget) this.contactEmailTarget.required = addingAuthorizedContact
    if (this.hasContactEmailHelpTarget) {
      this.contactEmailHelpTarget.textContent = addingAuthorizedContact
        ? "Required for this contact."
        : "Required when this contact is added."
    }
    this.contactPhoneTarget.required = phoneRequired
    this.contactPhoneHelpTarget.textContent = phoneRequired
      ? "Required because phone or text communication is selected."
      : "Required if phone or text communication is selected."

    if (this.hasContactChannelNoticeTarget) {
      this.contactChannelNoticeTarget.hidden = !phoneRequired || this.contactPhoneTarget.value.trim().length > 0
    }

    this.refreshAllInformationState()
  }

  updateAuthorizedContactCards(informationSharingAvailable) {
    if (!this.hasAuthorizedContactItemTarget) return

    this.authorizedContactItemTargets.forEach((item) => {
      const toggle = item.querySelector("[data-registration-form-target~='authorizedContactTypeToggle']")
      const panel = item.querySelector("[data-registration-form-target~='authorizedContactPanel']")
      const otherField = item.querySelector("[data-registration-form-target~='contactTypeOtherField']")
      const shouldShow = informationSharingAvailable && toggle?.checked

      item.hidden = !informationSharingAvailable
      item.classList.toggle("is-open", shouldShow)

      if (panel) this.togglePanel(panel, shouldShow)
      if (otherField) this.togglePanel(otherField, shouldShow && item.dataset.contactType === "other")
      if (toggle) toggle.disabled = !informationSharingAvailable
    })
  }

  updateContactPanelChannelRequirements(panel) {
    const capturesContactLead = this.panelCapturesContactLead(panel)
    const selectedChannels = Array.from(panel.querySelectorAll("input[name$='[allowed_channels][]']:checked"))
      .map((control) => control.value)
    const selectedCategories = Array.from(panel.querySelectorAll("input[name$='[allowed_categories][]']:checked"))
      .map((control) => control.value)
    const categoryFields = Array.from(panel.querySelectorAll("input[name$='[allowed_categories][]']"))
    const phoneRequired = !capturesContactLead && selectedChannels.some((channel) => channel === "phone" || channel === "sms")
    const emailField = panel.querySelector("[data-registration-form-target~='contactEmail']")
    const emailHelp = panel.querySelector("[data-registration-form-target~='contactEmailHelp']")
    const phoneField = panel.querySelector("[data-registration-form-target~='contactPhone']")
    const phoneHelp = panel.querySelector("[data-registration-form-target~='contactPhoneHelp']")
    const notice = panel.querySelector("[data-registration-form-target~='contactChannelNotice']")

    panel.querySelectorAll("[data-registration-required-when-visible]").forEach((field) => {
      field.required = !capturesContactLead
    })
    if (emailField) emailField.required = !capturesContactLead
    if (emailHelp) {
      emailHelp.textContent = capturesContactLead ? "" : "Required for this contact."
      emailHelp.hidden = capturesContactLead
    }
    if (phoneField) phoneField.required = phoneRequired
    if (phoneHelp) {
      if (capturesContactLead) {
        phoneHelp.textContent = ""
        phoneHelp.hidden = true
      } else {
        phoneHelp.textContent = phoneRequired
          ? "Required because phone or text communication is selected."
          : "Required if phone or text communication is selected."
        phoneHelp.hidden = false
      }
    }
    if (notice && phoneField) {
      notice.hidden = !phoneRequired || phoneField.value.trim().length > 0
    }

    categoryFields.forEach((field) => {
      field.setCustomValidity(capturesContactLead || selectedCategories.length > 0 ? "" : "Choose at least one information type.")
    })

    this.refreshAllInformationState()
  }

  panelCapturesContactLead(panel) {
    return this.panelContactType(panel) === "physician"
  }

  panelContactType(panel) {
    const item = panel.closest("[data-contact-type]")
    const baseType = item?.dataset.contactType

    if (baseType === "other") {
      return panel.querySelector("select[name$='[contact_type_other]']")?.value || baseType
    }

    return baseType
  }

  selectedRegistrantKind() {
    return this.registrantKindTargets.find((control) => control.checked)?.value || null
  }

  selectedAuthorizedContactValue() {
    if (this.hasAuthorizedContactTypeToggleTarget && this.authorizedContactTypeToggleTargets.some((control) => control.checked)) return "1"
    if (this.hasAuthorizedContactToggleTarget) {
      return this.authorizedContactToggleTargets.find((control) => control.checked)?.value || this.authorizedContactToggleTarget.value || "0"
    }

    return "0"
  }

  subjectAge() {
    if (!this.hasDobTarget || !this.dobTarget.value) return null

    const dob = new Date(`${this.dobTarget.value}T00:00:00`)
    if (Number.isNaN(dob.getTime())) return null

    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const birthdayHasPassed =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate())

    if (!birthdayHasPassed) age -= 1

    return age
  }
}
